import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/App";
import { Reveal, ChapterHead } from "@/components/Section";

const IMG_SHEEP = "https://prieure-saint-leonard.fr/api/files/prieure-saint-leonard/uploads/2702878b-0556-4d7d-93b6-80052672444d.jpg";

const Book = ({ onOrder }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <section id="livre" ref={ref} data-testid="book-crapaud-berger-section" className="relative px-5 md:px-10 py-28 md:py-44 bg-inkdeep overflow-hidden scroll-mt-20">
      <div className="absolute inset-0 opacity-[0.07]">
        <motion.img src={IMG_SHEEP} alt="" style={{ y }} className="w-full h-[120%] object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-inkdeep via-transparent to-inkdeep" />

      <div className="relative max-w-[1600px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <ChapterHead kicker={t.book.kicker} title={t.book.title} dark testId="book" />
            <Reveal delay={0.1}>
              <p data-testid="book-subtitle" className="font-display italic text-2xl md:text-3xl text-gold -mt-8 md:-mt-12 mb-4">
                {t.book.subtitle}
              </p>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-terraBright mb-10">{t.book.badge}</p>
              <p data-testid="book-intro" className="font-display text-xl md:text-2xl text-paper/85 leading-relaxed max-w-2xl mb-6">
                {t.book.intro}
              </p>
              <div className="space-y-6 max-w-xl">
                {t.book.paragraphs.map((p, i) => (
                  <p key={i} data-testid={`book-paragraph-${i}`} className="text-paper/70 text-base md:text-lg leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-6 mt-10">
                <button
                  data-testid="book-order-button"
                  onClick={onOrder}
                  className="group inline-flex items-center gap-3 bg-terra text-paper hover:bg-terraBright transition-colors px-8 py-4 font-mono2 text-[11px] uppercase tracking-[0.2em]"
                >
                  {t.book.cta}
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <Link
                  data-testid="book-read-online"
                  to="/lecture"
                  className="inline-flex items-center gap-3 border border-gold/60 text-gold hover:bg-gold hover:text-inkdeep transition-colors px-8 py-4 font-mono2 text-[11px] uppercase tracking-[0.2em]"
                >
                  {t.book.readCta}
                </Link>
                <p data-testid="book-meta" className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-paper/40">
                  {t.book.meta}
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="relative">
                <div className="absolute -inset-3 border border-gold/40 translate-x-4 translate-y-4" />
                <div className="relative overflow-hidden aspect-[5/7] bg-parchment shadow-[0_40px_100px_-30px_rgba(0,0,0,0.8)]">
                  <img src="/images/couverture-crapaud-berger.png" alt="Couverture — Le Crapaud et le Berger, VIVA ARTIST" className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-[1600ms] ease-out" />
                  <span className="absolute top-5 left-5 bg-gold text-inkdeep font-mono2 text-[9px] uppercase tracking-[0.25em] px-3 py-1.5">
                    2026
                  </span>
                </div>
                <p data-testid="book-image-caption" className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-paper/40 mt-6 text-right">
                  {t.book.imgCaption}
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-14 mt-24 md:mt-32">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-gold mb-8">{t.book.quotesTitle}</p>
              <div className="space-y-8">
                {t.book.quotes.map((q, i) => (
                  <blockquote key={i} data-testid={`book-quote-${i}`} className="border-l-2 border-gold/60 pl-6">
                    <p className="font-display italic text-xl md:text-2xl text-paper/85 leading-snug">{q.text}</p>
                    <cite className="not-italic font-mono2 text-[10px] uppercase tracking-[0.2em] text-terraBright block mt-3">{q.who}</cite>
                  </blockquote>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-gold mb-8">{t.book.charactersTitle}</p>
              <div className="grid sm:grid-cols-2 gap-px bg-linedark/60 border border-linedark/60" data-testid="book-characters">
                {t.book.characters.map((c, i) => (
                  <div key={i} data-testid={`book-character-${i}`} className="bg-inkdeep p-5 hover:bg-[#1E1915] transition-colors">
                    <p className="font-display text-lg text-paper leading-tight">{c.name}</p>
                    <p className="text-xs text-paper/50 mt-1.5 leading-relaxed">{c.role}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.15} className="mt-24 md:mt-28">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-gold mb-8">{t.book.agenda.title}</p>
          <div className="grid md:grid-cols-3 gap-px bg-linedark/60 border border-linedark/60" data-testid="book-agenda">
            {t.book.agenda.venues.map((v, i) => (
              <div key={i} data-testid={`agenda-venue-${i}`} className="bg-inkdeep p-7 hover:bg-[#1E1915] transition-colors">
                <MapPin size={16} className="text-terraBright mb-5" />
                <p className="font-display text-2xl text-paper leading-tight">{v.name}</p>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-paper/45 mt-3">{v.detail}</p>
              </div>
            ))}
          </div>
          <p data-testid="agenda-note" className="font-display italic text-xl md:text-2xl text-gold/80 mt-8">
            {t.book.agenda.note}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Book;
